import { useEffect, useState, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { debounce } from 'lodash'
import { SearchElement, Dropdown, Avatar } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { ContactsDropdownContent } from 'widgets/SearchDropdown/ui/DropdownContent/ContactsDropdownContent/ContactsDropdownContent'
import { type SearchDropdownContactsStore } from 'widgets/SearchDropdown/store/searchDropdownContactsStore'
import styles from './styles.module.scss'

type ContactsElementProps = {
  parentsDropdownId?: string[]
  store: SearchDropdownContactsStore
  onRemove: (type: SearchTypesEnum) => void
  activeType: SearchTypesEnum | null
  canShowChildDropdown: boolean
}

export const ContactsElement: FC<ContactsElementProps> = observer(
  ({ parentsDropdownId, store, onRemove, activeType, canShowChildDropdown }) => {
    const { contactsStore } = useEntitiesContext()
    const { hasSelectedItems, selectedItemsCount, firstSelectedItemId, selectedItems } = store
    const [show, setShow] = useState(false)

    const onShow = () => {
      const showDropdown = activeType === SearchTypesEnum.contacts && canShowChildDropdown

      setShow(showDropdown)
    }

    const showDebounce = debounce(onShow, 300)

    useEffect(() => {
      showDebounce()
    }, [canShowChildDropdown, activeType])

    const Label = observer(() => {
      const firstItem = firstSelectedItemId ? contactsStore.getItem(firstSelectedItemId) : null

      if (hasSelectedItems && selectedItemsCount === 1 && firstItem) {
        return (
          <div className={styles.container}>
            <div className={styles.avatar}>
              <Avatar size={16} info={firstItem?.avatarInfo} />
            </div>
            <span className={styles.selectedLabel}>{firstItem.name}</span>
          </div>
        )
      }

      return (
        <div className={styles.container}>
          <div
            className={styles.avatars}
            style={{ width: (selectedItemsCount < 3 ? selectedItemsCount : 3) * 12 }}
          >
            {selectedItems.slice(0, 3).map((item, idx) => (
              <div className={styles.avatar} key={item.id} style={{ left: idx * 12, zIndex: idx }}>
                <Avatar size={16} info={item.avatarInfo} />
              </div>
            ))}
          </div>
          <span className={styles.selectedLabel}>{selectedItemsCount} contacts</span>
        </div>
      )
    })

    return (
      <Dropdown
        ariaLabel={'ContactsElement'}
        parentsDropdownId={parentsDropdownId}
        iconButtonProps={{
          size: 'medium',
          fontSize: 20,
          ariaLabel: 'ContactsElement_trigger',
        }}
        show={show}
        triggerComponent={(open) => (
          <SearchElement
            icon={'users'}
            type='Contact'
            contentDefaultValue='select'
            warningContent={!open && !hasSelectedItems}
            removeCallback={() => {
              onRemove(SearchTypesEnum.contacts)
            }}
            content={hasSelectedItems && <Label />}
          />
        )}
        placement={'bottom-start'}
        sizeTriggerButton={'medium'}
        variantTriggerButton={'icon'}
        customComponent={() => <ContactsDropdownContent store={store} />}
      />
    )
  }
)
